import React from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Card } from 'react-native-elements';
import { colors, spacing, typography } from '../styles/globalStyles';
import type { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '../navigation/AppNavigation';

type GamesScreenNavigationProp = StackNavigationProp<RootStackParamList, 'GamesList'>;

const games = [
  {
    id: 'sayitslayit',
    title: 'Say It & Slay It',
    description: 'Challenge a friend to a turn-based speed reading competition!',
    icon: '⚡',
    route: 'SayItSlayIt',
    available: true
  },
  {
    id: 'echochamber',
    title: 'Echo Chamber',
    description: 'Test your memory by repeating an ever-growing sequence of sight words.',
    icon: '🎤',
    route: 'EchoChamber',
    available: true
  },
  {
    id: 'storychaos',
    title: 'Story Chaos',
    description: 'Collaborative storytelling with sight words!',
    icon: '📖💥',
    route: 'StoryChaos',
    available: true
  },
  {
    id: 'vocabularymaster',
    title: 'Vocabulary Master',
    description: 'Master new words through fun challenges',
    icon: '📚',
    route: 'VocabularyMaster',
    available: false
  }
];

export default function GamesScreen() {
  console.log('🎮 RENDERING GAMES SCREEN 🎮');
  const navigation = useNavigation<GamesScreenNavigationProp>();

  const handleGamePress = (game: typeof games[0]) => {
    console.log('Game pressed:', game.title);
    console.log('Route:', game.route);
    
    if (!game.available) {
      console.log('Game not available');
      return;
    }
    
    try {
      console.log('Attempting navigation to:', game.route);
      navigation.navigate(game.route as keyof RootStackParamList);
      console.log('Navigation completed');
    } catch (error) {
      console.error('Navigation error:', error);
    }
  };

  const renderGameCard = (game: typeof games[0]) => (
    <TouchableOpacity
      key={game.id}
      onPress={() => handleGamePress(game)}
      disabled={!game.available}
      style={styles.cardWrapper}
    >
      <Card containerStyle={styles.card}>
        <View style={styles.gameContent}>
          <View style={styles.iconContainer}>
            <Text style={styles.gameIcon}>{game.icon}</Text>
          </View>
          <View style={styles.gameInfo}>
            <Text style={styles.gameTitle}>{game.title}</Text>
            <Text style={styles.gameDescription}>{game.description}</Text>
            {!game.available && (
              <Text style={styles.comingSoon}>Coming Soon!</Text>
            )}
          </View>
        </View>
      </Card>
    </TouchableOpacity>
  );

  return (
    <ScrollView 
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
    >
      <View style={styles.headerContainer}>
        <Text style={styles.title}>Choose Your Game</Text>
      </View>
      <View style={styles.gamesContainer}>
        {games.map(renderGameCard)}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  contentContainer: {
    paddingBottom: spacing.xl,
  },
  headerContainer: {
    paddingVertical: spacing.xl,
  },
  title: {
    ...typography.title,
    textAlign: 'center',
    color: colors.primary,
  },
  gamesContainer: {
    paddingHorizontal: spacing.sm,
  },
  cardWrapper: {
    marginBottom: spacing.sm,
  },
  card: {
    borderRadius: 10,
    margin: 0,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 3,
      },
      web: {
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
      },
    }),
  },
  gameContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconContainer: {
    padding: spacing.md,
    justifyContent: 'center',
    alignItems: 'center',
    width: 60,
  },
  gameIcon: {
    fontSize: 24,
  },
  gameInfo: {
    flex: 1,
    paddingLeft: spacing.md,
  },
  gameTitle: {
    ...typography.subtitle,
    marginBottom: spacing.xs,
    color: colors.text.primary,
  },
  gameDescription: {
    ...typography.body,
    color: colors.text.secondary,
    marginBottom: spacing.sm,
  },
  comingSoon: {
    color: colors.warning,
    fontWeight: 'bold',
    fontSize: 12,
  },
}); 